import {useCallback, useEffect, useRef} from 'react';

import {clearMentionMeFiredTag, insertScript, serializeParams} from '@optimaxdev/utils';

import {TARGETING_COOKIES} from 'constants/analytics';
import {useCookieActiveGroup} from 'desktop/hooks/useCookieActiveGroup/useCookieActiveGroup';

import {ParamsType, PropsMentionType} from '../mentionMeTypes';

import {useGetDataForRequestMentionMe} from './useGetDataForRequestMentionMe';

/**
 * Mention me script generator
 */
export const useMentionMe = (
    params: ParamsType,
    integratingType: PropsMentionType['integratingType'],
) => {
    const {isFulfilled, ...dataForRequest} = useGetDataForRequestMentionMe(params.situation);
    const {isCookieAvailable} = useCookieActiveGroup(TARGETING_COOKIES);
    const shouldHide =
        (params && Object.keys(params).length === 0) || !isCookieAvailable || !isFulfilled;

    const scriptRef = useRef<HTMLScriptElement | null>(null);

    useEffect(() => {
        // Removes the previously inserted MentionMe script from the DOM when component unmounts
        return () => {
            scriptRef.current?.remove();
        };
    }, []);

    /**
     * After rendering the component, get and embed the pixel received from MentionMe
     */
    const generateScript = useCallback(() => {
        if (shouldHide) return;
        const mentionMeHost =
            process.env.APP_ENV === 'production' ? 'tag.mention-me.com' : 'tag-demo.mention-me.com';

        const src = `https://${mentionMeHost}/api/v2/${integratingType}/mm17dfe6fc?${serializeParams(
            {
                ...params,
                ...dataForRequest,
            },
        )}`;
        clearMentionMeFiredTag(params.implementation, params.situation);
        scriptRef.current = insertScript(src, true);
    }, [dataForRequest, integratingType, params, shouldHide]);

    return {generateScript, isCookieAvailable, isFulfilled, shouldHide};
};
